import React, { Fragment, useMemo, useState } from 'react';

import axios from 'axios';

import { parseLocaleNumber } from 'Helpers/currencyFormat';
import { formatFigure, formatSeparateByThousands, formatMoneyWithLocale } from 'Helpers/formatService';

import Button from 'CommonComponents/Button';

import { useToast } from 'Hooks/useToast';

import ChevronLeftIcon from 'Assets/icons/chevron-left.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';

// Allow editing quantities only when expense type is 'expense' and venues are missing/empty
// Venue prices come straight out of stored amount_data, so a string is parsed the way formatMoneyWithLocale reads it.
const moneyFigure = (value) => formatFigure(typeof value === 'string' ? parseLocaleNumber(value) : value, formatMoneyWithLocale, { precision: 3 });
const quantityFigure = (value) => formatFigure(value, formatSeparateByThousands, { precision: 3 });

function isExpenseEditable(expense) {
    const expenseType = (expense?.type ?? '').toString().toLowerCase();
    const venues = expense?.venues;
    const venuesMissing = venues == null;
    const venuesIsEmptyArray = Array.isArray(venues) && venues.length === 0;
    const venuesIsEmptyObject = !Array.isArray(venues) && typeof venues === 'object' && Object.keys(venues).length === 0;
    const noVenues = venuesMissing || venuesIsEmptyArray || venuesIsEmptyObject;

    return expenseType === 'expense' && noVenues;
}

const PACK_LINE_TYPE = 'pack';

/**
 * Whether a line is a row in its own right rather than a route to its sub-lines. The render and
 * the id lookup below both read this, so the rows on screen and the lines an edit can address
 * cannot drift apart.
 *
 * A pack is always a row: a contractor pack's total is overwritten with its own committed
 * cashflow and then no longer equals the sum of its children, so listing the children in its
 * place would print rows that do not add up to the foot Total.
 */
function isFlattenedLeaf(line) {
    return !line.subLines?.length || (line.type ?? '').toString().toLowerCase() === PACK_LINE_TYPE;
}

/**
 * One row per line item. The payload nests department → cost centre → line → sub-line; the table
 * shows none of that nesting, so each row carries the department trail it was found under as its
 * own cell value.
 *
 * `key` comes from the position in the tree rather than `line.id`: a project's Unallocated
 * sub-line reuses the project expense's id, so ids repeat within a single tree.
 */
export function flattenExpensesTree(departments) {
    const rows = [];

    const pushLine = (line, departmentLabel, path) => {
        if (!isFlattenedLeaf(line)) {
            line.subLines.forEach((subLine, index) => pushLine(subLine, departmentLabel, `${path}-s${index}`));

            return;
        }

        rows.push({ key: path, line, departmentLabel });
    };

    const pushDepartment = (department, trail, path) => {
        const departmentLabel = [...trail, department.name].filter(Boolean).join(' / ');

        (department.lines || []).forEach((line, index) => pushLine(line, departmentLabel, `${path}-l${index}`));

        (department.subDepartments || []).forEach((subDepartment, index) => pushDepartment(subDepartment, [...trail, department.name], `${path}-c${index}`));
    };

    (departments || []).forEach((department, index) => pushDepartment(department, [], `d${index}`));

    return rows;
}

export default function SupplierContractTermExpensesTable({
    treeIndex = 0,
    updateQuantitiesUrl = '',
    contract,
    subContractId = null,
    expensesTree,
    total,
    amounts,
    venues,
    onQuantitiesUpdated,
    onCloseModal,
    onRefreshContracts,
    maxVenuesQuantityTypes,
    onRedirectToPaymentMilestones,
}) {
    if (!expensesTree?.departments?.length) {
        return null;
    }

    const toast = useToast();
    const [showVenues, setShowVenues] = useState(true);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedAmounts, setEditedAmounts] = useState({});
    const [auditLogs, setAuditLogs] = useState([]);
    const [editSessionId, setEditSessionId] = useState(null);
    const [expenseChanges, setExpenseChanges] = useState({}); // Track changes per expense
    const [isSaving, setIsSaving] = useState(false); // Loading state for save operation

    const amountNames = useMemo(() => (amounts ? Object.keys(amounts) : []), [amounts]);
    const venuesIds = useMemo(() => (venues ? Object.keys(venues) : []), [venues]);
    const rows = useMemo(() => flattenExpensesTree(expensesTree.departments), [expensesTree]);

    // Columns sitting between the sticky Item column and Total: department / cost centre, type,
    // price, then one per amount name.
    const colSpan = useMemo(() => 3 + amountNames.length, [amountNames]);

    // Function to create audit logs from tracked changes
    const createAuditLogsFromChanges = () => {
        const logs = [];

        Object.entries(expenseChanges).forEach(([expenseId, changes]) => {
            const { originalQuantities, revisedQuantities, expense } = changes;

            // Create a separate audit log for each quantity change
            Object.keys(revisedQuantities).forEach((amountName) => {
                const originalAmount = originalQuantities[amountName] || 0;
                const revisedAmount = revisedQuantities[amountName] || 0;

                // Only create log if there are actual changes
                if (originalAmount !== revisedAmount) {
                    // Calculate variation for this specific quantity
                    const variance = revisedAmount - originalAmount;
                    const totalVariation = variance * (expense.price || 0);

                    logs.push({
                        contract_id: contract?.id,
                        sub_contract_id: subContractId,
                        expense_id: expense.id,
                        amount_name: amountName,
                        original_quantity: [{ name: amountName, amount: originalAmount }],
                        revised_quantity: [{ name: amountName, amount: revisedAmount }],
                        price: expense.price || 0,
                        total_variation: totalVariation,
                        edit_session_id: editSessionId,
                    });
                }
            });
        });

        return logs;
    };

    // Check if there are any quantity changes
    const hasChanges = useMemo(() => {
        const auditLogs = createAuditLogsFromChanges();
        return auditLogs.length > 0;
    }, [expenseChanges, editedAmounts]);

    const handleEditQuantities = () => {
        setIsEditMode(true);
        // Initialize edited amounts with current values
        const initialEditedAmounts = {};
        // This will be populated when we traverse the expenses tree
        setEditedAmounts(initialEditedAmounts);
        // Clear previous audit logs when starting new edit session
        setAuditLogs([]);
        setExpenseChanges({});
        // Generate unique session ID for this edit session
        setEditSessionId(crypto.randomUUID());
    };

    const handleSaveQuantities = () => {
        // Create audit logs from tracked changes first
        const auditLogs = createAuditLogsFromChanges();

        // Validate that at least one quantity has been changed
        if (auditLogs.length === 0) {
            console.warn('No quantity changes detected. Cannot save without changes.');
            return;
        }

        // Calculate the new total value after quantity changes
        const updatedExpenseTree = createUpdatedExpenseTree();
        const newTotal = updatedExpenseTree.total || 0;
        const originalTotal = expensesTree.total || 0;
        const hasReducedValue = newTotal < originalTotal;

        if (hasReducedValue) {
            // If value has been reduced, redirect to Payment Milestones tab
            if (onRedirectToPaymentMilestones && typeof onRedirectToPaymentMilestones === 'function') {
                onRedirectToPaymentMilestones({
                    originalTotal,
                    newTotal,
                    updatedExpenseTree,
                    editedAmounts,
                    auditLogs,
                });
            }
        } else {
            // If value hasn't been reduced, proceed with normal save
            performSave();
        }
    };

    const performSave = (saveData = null) => {
        // Set loading state
        setIsSaving(true);

        // Use provided save data or create new one
        const dataToSave = saveData || {
            originalExpensesTree: expensesTree,
            editedExpensesTree: createUpdatedExpenseTree(),
            editedAmounts: editedAmounts,
            auditLogs: createAuditLogsFromChanges(),
        };

        const url = updateQuantitiesUrl.replace('CONTRACT_ID', contract?.id);
        axios
            .post(url, dataToSave)
            .then((response) => {
                // Reset edit state
                setIsEditMode(false);
                setEditedAmounts({});
                setAuditLogs([]);
                setExpenseChanges({});
                setEditSessionId(null);

                // Show success message
                toast.success($t('Contract quantities updated successfully'));

                // Trigger contract terms refresh if callback provided
                if (onQuantitiesUpdated && typeof onQuantitiesUpdated === 'function') {
                    onQuantitiesUpdated();
                }

                // Close modal and refresh contracts list after successful update
                if (onCloseModal && typeof onCloseModal === 'function') {
                    onCloseModal();
                }

                if (onRefreshContracts && typeof onRefreshContracts === 'function') {
                    onRefreshContracts();
                }
            })
            .catch((error) => {
                console.error('Error updating quantities:', error);
                // A line with a budget change pending approval refuses quantity changes, saying why.
                toast.error(error?.response?.data?.errors?.pending_line_change?.[0] ?? $t('An error occurred while updating quantities. Please try again.'));
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEditedAmounts({});
        setAuditLogs([]);
        setExpenseChanges({});
        setEditSessionId(null);
    };

    const trackExpenseChange = (expenseId, amountName, originalQuantity, revisedQuantity) => {
        const expense = findExpenseInTree(expensesTree.departments, expenseId);
        if (!expense) return;

        setExpenseChanges((prev) => {
            const currentChanges = prev[expenseId] || {
                originalQuantities: {},
                revisedQuantities: {},
                expense: expense,
            };

            // Store original quantity (only if not already stored)
            if (currentChanges.originalQuantities[amountName] === undefined) {
                currentChanges.originalQuantities[amountName] = originalQuantity;
            }

            // Store revised quantity
            currentChanges.revisedQuantities[amountName] = revisedQuantity;

            return {
                ...prev,
                [expenseId]: currentChanges,
            };
        });
    };

    // Resolves exactly the lines the flat list renders, at any depth. Containers are searched
    // through rather than matched, so an id shared by a container and one of its rows — a
    // project and its Unallocated sub-line carry the same expense id — resolves to the row.
    const findExpenseInTree = (departments, targetId) => {
        const findInLines = (lines) => {
            for (const line of lines || []) {
                if (!isFlattenedLeaf(line)) {
                    const match = findInLines(line.subLines);
                    if (match) {
                        return match;
                    }

                    continue;
                }

                if (line.id === targetId) {
                    return line;
                }
            }

            return null;
        };

        const findInDepartments = (list) => {
            for (const department of list || []) {
                const match = findInLines(department.lines) || findInDepartments(department.subDepartments);
                if (match) {
                    return match;
                }
            }

            return null;
        };

        return findInDepartments(departments);
    };

    const handleAmountChange = (expenseId, amountName, newValue) => {
        const currentAmount = getCurrentAmount(expenseId, amountName);
        const numericNewValue = parseFloat(newValue) || 0;

        // Only allow downgrading (new value should be less than or equal to current)
        if (numericNewValue <= currentAmount) {
            const previousEditedAmount = editedAmounts[`${expenseId}-${amountName}`];
            const originalAmount = previousEditedAmount !== undefined ? previousEditedAmount : currentAmount;
            setEditedAmounts((prev) => ({
                ...prev,
                [`${expenseId}-${amountName}`]: numericNewValue,
            }));

            // Track the change for this expense
            trackExpenseChange(expenseId, amountName, originalAmount, numericNewValue);
        }
    };

    const getCurrentAmount = (expenseId, amountName) => {
        const expense = findExpenseInTree(expensesTree.departments, expenseId);
        return expense?.amounts?.[amountName] || 0;
    };

    const getEditedAmount = (expenseId, amountName) => {
        const key = `${expenseId}-${amountName}`;
        return editedAmounts[key] !== undefined ? editedAmounts[key] : null;
    };

    const createUpdatedExpenseTree = () => {
        // Deep clone the original expense tree
        const updatedTree = JSON.parse(JSON.stringify(expensesTree));

        // Function to update a single expense line and calculate its total
        const updateExpenseLine = (line) => {
            let hasChanges = false;

            // Update amounts with edited values
            if (line.amounts) {
                Object.keys(line.amounts).forEach((amountName) => {
                    const editedAmount = getEditedAmount(line.id, amountName);
                    if (editedAmount !== null) {
                        line.amounts[amountName] = editedAmount;
                        hasChanges = true;
                    }
                });

                // Recalculate line total based on updated amounts and price
                if (hasChanges && line.price) {
                    const totalAmount = Object.values(line.amounts).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
                    line.total = totalAmount * line.price;
                }
            }

            // Update sublines recursively and calculate their totals
            if (line.subLines && line.subLines.length > 0) {
                let subLinesTotal = 0;
                line.subLines.forEach((subLine) => {
                    updateExpenseLine(subLine);
                    subLinesTotal += subLine.total || 0;
                });

                // If this line has subLines, its total should be the sum of subLines totals
                // Only update if the line doesn't have amounts (i.e., it's a parent line)
                if (!line.amounts || Object.keys(line.amounts).length === 0) {
                    line.total = subLinesTotal;
                }
            }

            return hasChanges;
        };

        // Function to update department totals
        const updateDepartmentTotals = (department) => {
            let departmentTotal = 0;

            // Update lines in this department
            if (department.lines) {
                department.lines.forEach((line) => {
                    updateExpenseLine(line);
                    departmentTotal += line.total || 0;
                });
            }

            // Update subdepartments
            if (department.subDepartments) {
                department.subDepartments.forEach((subDept) => {
                    updateDepartmentTotals(subDept);
                    departmentTotal += subDept.total || 0;
                });
            }

            department.total = departmentTotal;
        };

        // Update all departments
        if (updatedTree.departments) {
            updatedTree.departments.forEach((department) => {
                updateDepartmentTotals(department);
            });
        }

        // Recalculate the overall tree total
        if (updatedTree.departments) {
            updatedTree.total = updatedTree.departments.reduce((sum, department) => sum + (department.total || 0), 0);
        }

        return updatedTree;
    };

    return (
        <div className="tw-max-h-full tw-flex tw-flex-col">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
                <h4 className="table__header">{treeIndex >= 1 ? $t('Contract Variation') + ' ' + treeIndex : $t('Expenses')}</h4>
                <div className="tw-flex tw-gap-2 tw-me-2">
                    {!isEditMode ? (
                        <Button variant="primary" onClick={handleEditQuantities}>
                            {$t('Edit Quantities')}
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="primary"
                                onClick={handleSaveQuantities}
                                disabled={isSaving || !hasChanges}
                                className={isSaving || !hasChanges ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}
                            >
                                {isSaving ? (
                                    <span className="tw-flex tw-items-center tw-gap-2">
                                        <svg
                                            className="tw-animate-spin tw-h-4 tw-w-4 tw-text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path
                                                className="tw-opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        {$t('Saving...')}
                                    </span>
                                ) : !hasChanges ? (
                                    $t('No Changes')
                                ) : (
                                    $t('Save')
                                )}
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={handleCancelEdit}
                                disabled={isSaving}
                                className={isSaving ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}
                            >
                                {$t('Cancel')}
                            </Button>
                        </>
                    )}
                </div>
            </div>
            <div className="inbud-table_wrapper tw-overflow-y-auto tw-h-full tw-min-h-0">
                <table className="inbud-table tw-table-fixed inbud-table_first-sticky">
                    <thead className="tw-sticky tw-top-0 tw-z-10">
                        <tr>
                            <th rowSpan={2} className="tw-w-52 tw-z-10">
                                {$t('Item')}
                            </th>
                            <th rowSpan={2} className="tw-w-52">
                                {$t('Department / Cost Centre')}
                            </th>
                            <th rowSpan={2} className="tw-w-24">
                                {$t('Type')}
                            </th>
                            <th rowSpan={2} className="tw-w-28">
                                {$t('Price')}
                            </th>
                            {!!amountNames?.length && (
                                <Fragment>
                                    {amountNames?.map((name) => (
                                        <th rowSpan={2} className="tw-w-28" key={name}>
                                            {name}
                                        </th>
                                    ))}
                                </Fragment>
                            )}
                            <th rowSpan={2} className="tw-w-44">
                                {$t('Total')}
                            </th>
                            {!!venuesIds?.length && (
                                <th rowSpan={2} className="tw-w-48 text-center">
                                    {$t('Venues Allocations')}
                                    {!!venuesIds?.length && (
                                        <button type="button" onClick={() => setShowVenues((state) => !state)} title="Toggle Venues">
                                            {showVenues ? <ChevronLeftIcon className="tw-h-3 tw-ms-2" /> : <ChevronRightIcon className="tw-h-3 tw-ms-2" />}
                                        </button>
                                    )}
                                </th>
                            )}
                            {showVenues &&
                                Object.values(venues).map((venue) => (
                                    <th colSpan={maxVenuesQuantityTypes + 2} className="tw-w-96 tw-text-center tw-border-b" key={venue.id}>
                                        {venue.name}
                                    </th>
                                ))}
                        </tr>
                        {showVenues && (
                            <tr>
                                {Object.values(venues).map((venue) => (
                                    <Fragment key={venue.id}>
                                        {/* Create quantity columns based on maxVenuesQuantityTypes */}
                                        {Array.from({ length: maxVenuesQuantityTypes }, (_, index) => (
                                            <th key={`quantity_${index + 1}`} className="tw-w-28 tw-text-center tw-border">
                                                {$t('Quantity')} {index + 1}
                                            </th>
                                        ))}
                                        <th className="tw-w-28 tw-text-center tw-border">{$t('Price')}</th>
                                        <th className="tw-w-28 tw-text-center tw-border">{$t('Total')}</th>
                                    </Fragment>
                                ))}
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <SupplierContractTermExpensesTableRow
                                key={row.key}
                                expense={row.line}
                                departmentLabel={row.departmentLabel}
                                amountNames={amountNames}
                                venuesIds={venuesIds}
                                showVenues={showVenues}
                                maxVenuesQuantityTypes={maxVenuesQuantityTypes}
                                isEditMode={isEditMode}
                                handleAmountChange={handleAmountChange}
                                getEditedAmount={getEditedAmount}
                            />
                        ))}

                        <tr>
                            <td className="!tw-border-r-0" />
                            <td colSpan={colSpan} />
                            <td className="inbud-numeric-cell">{moneyFigure(total)}</td>
                            {!!venuesIds?.length && <td />}
                            {!!venuesIds?.length && showVenues && <td colSpan={venuesIds.length * (maxVenuesQuantityTypes + 2)} />}
                            {/* +2 for Price and Total columns, +1 for the Venues Allocations column */}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SupplierContractTermExpensesTableRow({
    expense,
    departmentLabel,
    amountNames,
    venuesIds,
    showVenues,
    isEditMode,
    handleAmountChange,
    getEditedAmount,
    maxVenuesQuantityTypes,
}) {
    return (
        <tr>
            {/* Fixed layout, so an unbroken item or department name would push its neighbours off
                the table rather than widen its own column. */}
            <td className="tw-break-words">{expense.name}</td>
            <td className="tw-break-words">{departmentLabel}</td>
            <td>{expense.transaction}</td>
            <td className="inbud-numeric-cell">{moneyFigure(expense.price)}</td>
            {!!amountNames?.length &&
                amountNames?.map((name) => {
                    const currentAmount = expense.amounts?.[name];
                    const editedAmount = getEditedAmount(expense.id, name);
                    const displayAmount = editedAmount !== null ? editedAmount : currentAmount;

                    return (
                        <td key={name} className="inbud-numeric-cell">
                            {isEditMode && currentAmount && isExpenseEditable(expense) ? (
                                <input
                                    type="number"
                                    value={displayAmount || ''}
                                    onChange={(e) => handleAmountChange(expense.id, name, e.target.value)}
                                    className="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded tw-text-end"
                                    min="0"
                                    max={currentAmount}
                                    step="0.01"
                                />
                            ) : (
                                quantityFigure(displayAmount)
                            )}
                        </td>
                    );
                })}
            <td className="inbud-numeric-cell">{moneyFigure(expense.total)}</td>
            {!!venuesIds?.length && <td />}
            {showVenues &&
                venuesIds.map((id) => {
                    return expense.venues?.[id] ? (
                        <Fragment key={id}>
                            {/* Create quantity columns based on maxVenuesQuantityTypes */}
                            {Array.from({ length: maxVenuesQuantityTypes }, (_, index) => {
                                const quantityKey = `quantity_${index + 1}`;
                                const venueData = expense.venues[id];
                                const amounts = venueData && venueData['amounts'];
                                const quantityValue = amounts ? amounts[quantityKey] : null;

                                return (
                                    <td key={quantityKey} className="tw-text-end !tw-bg-white inbud-numeric-cell">
                                        {quantityFigure(quantityValue)}
                                    </td>
                                );
                            })}
                            <td className="tw-text-end !tw-bg-white inbud-numeric-cell">{moneyFigure(expense.venues[id]['price'])}</td>
                            <td className="tw-text-end !tw-bg-white inbud-numeric-cell">{moneyFigure(expense.venues[id]['total'])}</td>
                        </Fragment>
                    ) : (
                        <Fragment key={id}>
                            {/* Create empty quantity columns based on maxVenuesQuantityTypes */}
                            {Array.from({ length: maxVenuesQuantityTypes }, (_, index) => (
                                <td key={`quantity_${index + 1}`} className="!tw-bg-white" />
                            ))}
                            <td className="!tw-bg-white" />
                            <td className="!tw-bg-white" />
                        </Fragment>
                    );
                })}
        </tr>
    );
}
